import type { ClassTimes, LessonOverride } from '@/types';

export const WEEK_DAYS = ['월', '화', '수', '목', '금', '토', '일'];

export interface ClassTimeSource {
  class_times?: ClassTimes | null;
  class_start_time?: string | null;
  class_end_time?: string | null;
}

export interface DayTime {
  day: string;
  start: string;
  end: string;
}

/** 특정 요일의 수업 시간을 가져와요. 요일별 시간이 없으면 기존 대표 시간으로 대체해요. */
export function getDayTime(
  student: ClassTimeSource,
  day: string,
  fallbackStart?: string | null,
  fallbackEnd?: string | null,
): { start: string; end: string } {
  const t = student.class_times?.[day];
  if (t && (t.start || t.end)) {
    return { start: t.start || '', end: t.end || '' };
  }
  return {
    start: fallbackStart ?? student.class_start_time ?? '',
    end: fallbackEnd ?? student.class_end_time ?? '',
  };
}

/** 수업 요일 순서대로 요일별 시간 목록을 만들어요. */
export function listDayTimes(student: ClassTimeSource, days: string[]): DayTime[] {
  return WEEK_DAYS.filter((d) => days.includes(d)).map((d) => ({
    day: d,
    ...getDayTime(student, d),
  }));
}

/** 특정 요일 수업 시작 시간(정렬용). 없으면 '99:99'. */
export function dayStartKey(student: ClassTimeSource, day: string): string {
  return getDayTime(student, day).start || '99:99';
}

/** 카드에 표시할 요일별 시간 요약 문자열 목록. */
export function dayTimeLabels(student: ClassTimeSource, days: string[]): string[] {
  return listDayTimes(student, days)
    .filter((t) => t.start)
    .map((t) => `${t.day} ${t.start}${t.end ? `~${t.end}` : ''}`);
}

/** 날짜별 시간 예외를 학생·날짜 키로 묶어요. */
export function overrideKey(studentId: string, date: string): string {
  return `${studentId}__${date}`;
}

/** 시간 예외 목록을 조회하기 쉬운 맵으로 변환해요. */
export function buildOverrideMap(
  overrides: LessonOverride[],
): Record<string, LessonOverride> {
  const map: Record<string, LessonOverride> = {};
  overrides.forEach((o) => {
    map[overrideKey(o.student_id, o.override_date)] = o;
  });
  return map;
}

export interface DateScopedSource extends ClassTimeSource {
  id: string;
}

/** 학생 카드/목록에서 쓰는 최소 수업 정보. */
export interface ScheduleLike extends ClassTimeSource {
  id: string;
  name?: string;
  class_days?: string[] | null;
  first_lesson_date?: string | null;
}

/** YYYY-MM-DD 날짜의 요일 한 글자를 돌려줘요. */
export function weekdayChar(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return WEEK_DAYS[(d.getDay() + 6) % 7];
}

/** 특정 날짜의 수업 시간을 계산해요. 그 날짜에 예외가 있으면 예외 시간을 우선해요. */
export function getTimeForDate(
  student: DateScopedSource,
  day: string,
  date: string,
  overrideMap: Record<string, LessonOverride>,
): { start: string; end: string; isOverride: boolean } {
  const o = overrideMap[overrideKey(student.id, date)];
  if (o && (o.start_time || o.end_time)) {
    return { start: o.start_time ?? '', end: o.end_time ?? '', isOverride: true };
  }
  const base = getDayTime(student, day);
  return { start: base.start, end: base.end, isOverride: false };
}

/** 해당 날짜가 학생의 첫 수업일보다 앞서면 true. (첫 수업 전에는 수업이 없어요) */
export function isBeforeFirstLesson(
  student: { first_lesson_date?: string | null },
  date: string,
): boolean {
  if (!student.first_lesson_date) return false;
  return date < student.first_lesson_date;
}

export type LessonStateKind = 'normal' | 'time' | 'cancel' | 'moved';

export interface LessonState {
  kind: LessonStateKind;
  start: string;
  end: string;
  isOverride: boolean;
  note: string | null;
  movedToDate: string | null;
  override: LessonOverride | null;
}

/**
 * 특정 날짜 수업의 상태를 계산해요.
 * - normal: 정규 수업
 * - time: 같은 날 시간만 변경
 * - moved: 다른 날짜로 이동(수업 일정 변경) → 이 날은 수업 없음
 * - cancel: 휴강 → 이 날은 수업 없음
 */
export function getLessonState(
  student: DateScopedSource,
  day: string,
  date: string,
  overrideMap: Record<string, LessonOverride>,
): LessonState {
  const base = getDayTime(student, day);
  const o = overrideMap[overrideKey(student.id, date)] ?? null;
  if (!o) {
    return {
      kind: 'normal',
      start: base.start,
      end: base.end,
      isOverride: false,
      note: null,
      movedToDate: null,
      override: null,
    };
  }
  if (o.kind === 'cancel') {
    return {
      kind: 'cancel',
      start: base.start,
      end: base.end,
      isOverride: false,
      note: o.note,
      movedToDate: null,
      override: o,
    };
  }
  if (o.kind === 'move') {
    return {
      kind: 'moved',
      start: base.start,
      end: base.end,
      isOverride: false,
      note: o.note,
      movedToDate: o.moved_to_date,
      override: o,
    };
  }
  return {
    kind: 'time',
    start: o.start_time,
    end: o.end_time,
    isOverride: true,
    note: o.note,
    movedToDate: null,
    override: o,
  };
}

/** 다른 날짜에서 이 날짜로 옮겨온(일정 변경된) 수업의 시간을 계산해요(비어 있으면 그 요일 정규 시간). */
export function movedInStartEnd(
  student: ClassTimeSource | undefined,
  date: string,
  o: LessonOverride,
): { start: string; end: string } {
  const day = weekdayChar(date);
  const base = student && day ? getDayTime(student, day) : { start: '', end: '' };
  return { start: o.start_time || base.start, end: o.end_time || base.end };
}

export interface DatedLessonItem {
  student: ScheduleLike;
  studentId: string;
  name: string;
  start: string;
  end: string;
  isMovedIn: boolean;
  isTimeChanged: boolean;
}

/**
 * 특정 날짜에 실제로 진행되는 수업 목록을 만들어요.
 * 날짜가 변경되거나 휴강된 수업은 원래 날에서 제외하고, 그 날로 옮겨온(일정 변경된) 수업은 포함해요.
 */
export function lessonsForDate(
  students: ScheduleLike[],
  date: string,
  overrideMap: Record<string, LessonOverride>,
  overrides: LessonOverride[] = [],
): DatedLessonItem[] {
  const day = weekdayChar(date);
  const byId = new Map(students.map((s) => [s.id, s]));
  const items: DatedLessonItem[] = [];

  students.forEach((s) => {
    if (!(s.class_days ?? []).includes(day)) return;
    if (isBeforeFirstLesson(s, date)) return;
    const state = getLessonState(s, day, date, overrideMap);
    if (state.kind === 'moved' || state.kind === 'cancel') return;
    items.push({
      student: s,
      studentId: s.id,
      name: s.name ?? '',
      start: state.start,
      end: state.end,
      isMovedIn: false,
      isTimeChanged: state.isOverride,
    });
  });

  overrides
    .filter((o) => o.kind === 'move' && o.moved_to_date === date)
    .forEach((o) => {
      const s = byId.get(o.student_id);
      if (!s) return;
      const t = movedInStartEnd(s, date, o);
      items.push({
        student: s,
        studentId: o.student_id,
        name: s.name ?? '',
        start: t.start,
        end: t.end,
        isMovedIn: true,
        isTimeChanged: false,
      });
    });

  return items.sort((a, b) => (a.start || '99:99').localeCompare(b.start || '99:99'));
}